import { DirectUpload, type DirectUploadBlob } from '@rails/activestorage'
import { sessionFromResponse } from '@/frontend/pages/Dashboard/AgentChats/sessionResponse'
import type {
  UserQuestionAnswer,
  AgentChatBackgroundRun,
  AgentChatSessionDetail,
  AgentChatWorkflowReference,
} from '@/frontend/pages/Dashboard/AgentChats/types'

interface SendOptions {
  questionAnswer?: UserQuestionAnswer
  attachmentSignedBlobIds?: string[]
  // O chat do Command Center envia anexos para a organização DONA do agente,
  // que não é a organização da sessão do navegador. O id do agente viaja junto
  // do upload para o servidor resolver essa organização.
  digitalWorkerId?: string | null
  clientSubmissionId?: string
  onUploadsComplete?: () => void
  responseTimeoutMs?: number
  signal?: AbortSignal
  pageContext?: Record<string, unknown>
}

export interface AgentChatHistorySession {
  id: string
  title: string
  last_message_at: string | null
}

export type AgentChatApiScope = '/dashboard' | '/cc'

/**
 * Prefixo das rotas de chat, derivado da superfície em que a página está
 * rodando: o chat do Command Center (`/cc/chat`) fala com `/cc/agent_chat_*`,
 * o chat do dashboard fala com `/dashboard/agent_chat_*`.
 *
 * É derivado da URL em vez de passado por parâmetro de propósito: as duas
 * superfícies nunca coexistem numa mesma página Inertia, e assim não existe
 * estado mutável de módulo nem 20 pontos de repasse para alguém esquecer. A
 * fronteira de verdade é o servidor — cada controller autoriza o próprio
 * escopo, então um prefixo errado falha fechado (403/404), não vaza dados.
 */
export function agentChatApiScope(): AgentChatApiScope {
  if (typeof window === 'undefined') return '/dashboard'

  return window.location.pathname.startsWith('/cc/') ? '/cc' : '/dashboard'
}

function chatPath(suffix: string): string {
  return `${agentChatApiScope()}${suffix}`
}

export async function createRealtimeTranscriptionSession(
  offerSdp: string,
  signal?: AbortSignal,
): Promise<string> {
  const response = await fetchWithCsrfRetry(chatPath('/agent_chat_transcriptions'), {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/sdp',
      'X-CSRF-Token': csrfToken(),
      'Content-Type': 'application/sdp',
    },
    body: offerSdp,
    signal,
  })

  if (!response.ok) {
    const payload = await response.json().catch(() => ({})) as { error?: string }
    throw new Error(payload.error || 'Não foi possível iniciar a transcrição.')
  }

  const answerSdp = await response.text()
  if (!answerSdp.trim()) throw new Error('A transcrição retornou uma resposta inválida.')

  return answerSdp
}

interface AgentChatHistoryWorker {
  digital_worker_id: string
  has_more: boolean
  sessions: AgentChatHistorySession[]
}

function csrfToken(): string {
  if (typeof document === 'undefined') return ''

  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
}

export async function loadSessionOverHttp(
  sessionId: string,
  options: { signal?: AbortSignal } = {},
): Promise<AgentChatSessionDetail> {
  const response = await fetch(`${chatPath('/agent_chat_sessions')}/${sessionId}`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    signal: options.signal,
  })

  return sessionFromResponse(response)
}

export async function loadSessionHistoryOverHttp(
  options: { signal?: AbortSignal } = {},
): Promise<AgentChatHistoryWorker[]> {
  const response = await fetch(chatPath('/agent_chat_sessions'), {
    method: 'GET',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    signal: options.signal,
  })
  const payload = await response.json().catch(() => ({})) as {
    error?: string
    workers?: AgentChatHistoryWorker[]
  }

  if (!response.ok || !Array.isArray(payload.workers)) {
    throw new Error(payload.error || 'Não foi possível carregar as conversas agora.')
  }

  return payload.workers
}

export async function renameSessionOverHttp(
  sessionId: string,
  title: string,
): Promise<AgentChatSessionDetail> {
  const response = await fetchWithCsrfRetry(`${chatPath('/agent_chat_sessions')}/${sessionId}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: requestHeaders(),
    body: JSON.stringify({ title }),
  })

  return sessionFromResponse(response)
}

export async function loadBackgroundRunOverHttp(
  sessionId: string,
  turnId: string,
  runId: string,
  options: { signal?: AbortSignal } = {},
): Promise<AgentChatBackgroundRun> {
  const path = `${chatPath('/agent_chat_sessions')}/${sessionId}/turns/${turnId}/background_runs/${encodeURIComponent(runId)}`
  const response = await fetch(path, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    signal: options.signal,
  })
  const payload = await response.json().catch(() => ({})) as {
    error?: string
    background_run?: AgentChatBackgroundRun
  }
  if (!response.ok || !payload.background_run) {
    throw new Error(payload.error || 'N\u00e3o foi poss\u00edvel carregar este trabalho agora.')
  }

  return payload.background_run
}

export async function pinSessionOverHttp(
  sessionId: string,
  pinned: boolean,
): Promise<AgentChatSessionDetail> {
  const response = await fetchWithCsrfRetry(`${chatPath('/agent_chat_sessions')}/${sessionId}/pin`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: requestHeaders(),
    body: JSON.stringify({ pinned }),
  })

  return sessionFromResponse(response)
}

export async function archiveSessionOverHttp(
  sessionId: string,
): Promise<AgentChatSessionDetail> {
  const response = await fetchWithCsrfRetry(`${chatPath('/agent_chat_sessions')}/${sessionId}/archive`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: requestHeaders(),
  })

  return sessionFromResponse(response)
}

export async function deleteSessionOverHttp(sessionId: string): Promise<void> {
  const response = await fetchWithCsrfRetry(`${chatPath('/agent_chat_sessions')}/${sessionId}`, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: requestHeaders(),
  })
  if (response.ok) return

  const payload = await response.json().catch(() => ({})) as { error?: string }
  throw new Error(payload.error || 'Não foi possível excluir a conversa agora.')
}

export function exportSessionUrl(sessionId: string): string {
  return `${chatPath('/agent_chat_sessions')}/${sessionId}/export`
}

export async function abortSessionOverHttp(
  sessionId: string,
): Promise<AgentChatSessionDetail> {
  const response = await fetchWithCsrfRetry(`${chatPath('/agent_chat_sessions')}/${sessionId}/abort`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: requestHeaders(),
  })

  return sessionFromResponse(response)
}

export async function retrySessionOverHttp(
  sessionId: string,
): Promise<AgentChatSessionDetail> {
  const response = await fetchWithCsrfRetry(`${chatPath('/agent_chat_sessions')}/${sessionId}/retry`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: requestHeaders(),
  })

  return sessionFromResponse(response)
}

export async function submitMessageFeedbackOverHttp(
  sessionId: string,
  messageId: string,
  score: 'positive' | 'negative',
): Promise<AgentChatSessionDetail> {
  const response = await fetchWithCsrfRetry(
    `${chatPath('/agent_chat_sessions')}/${sessionId}/messages/${messageId}/feedback`,
    {
      method: 'POST',
      credentials: 'same-origin',
      headers: requestHeaders(),
      body: JSON.stringify({ score }),
    },
  )

  return sessionFromResponse(response)
}

export async function branchSessionOverHttp({
  sessionId,
  messageId,
  mode,
  content,
}: {
  sessionId: string
  messageId: string
  mode: 'branch' | 'edit' | 'regenerate'
  content?: string
}): Promise<AgentChatSessionDetail> {
  const response = await fetchWithCsrfRetry(`${chatPath('/agent_chat_sessions')}/${sessionId}/branch`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: requestHeaders(),
    body: JSON.stringify({ message_id: messageId, mode, content }),
  })

  return sessionFromResponse(response)
}

export async function sendMessageOverHttp(
  sessionId: string,
  content: string,
  attachments: File[] = [],
  options: SendOptions = {},
): Promise<AgentChatSessionDetail> {
  throwIfAborted(options.signal)
  const attachmentSignedBlobIds = options.attachmentSignedBlobIds ?? await uploadAttachments(attachments, options.digitalWorkerId)
  options.attachmentSignedBlobIds = attachmentSignedBlobIds
  throwIfAborted(options.signal)
  options.onUploadsComplete?.()
  throwIfAborted(options.signal)

  return sessionFromSubmissionRequest(`${chatPath('/agent_chat_sessions')}/${sessionId}/messages`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: requestHeaders(),
    body: JSON.stringify(messagePayload({
      content,
      attachmentSignedBlobIds,
      clientSubmissionId: options.clientSubmissionId,
      pageContext: options.pageContext,
      questionAnswer: options.questionAnswer,
    })),
    signal: options.signal,
  }, options.responseTimeoutMs)
}

export async function steerSessionOverHttp(
  sessionId: string,
  content: string,
  attachments: File[] = [],
  options: SendOptions = {},
): Promise<AgentChatSessionDetail> {
  throwIfAborted(options.signal)
  const attachmentSignedBlobIds = options.attachmentSignedBlobIds ?? await uploadAttachments(attachments, options.digitalWorkerId)
  options.attachmentSignedBlobIds = attachmentSignedBlobIds
  throwIfAborted(options.signal)
  options.onUploadsComplete?.()

  return sessionFromSubmissionRequest(`${chatPath('/agent_chat_sessions')}/${sessionId}/steer`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: requestHeaders(),
    body: JSON.stringify(messagePayload({
      content,
      attachmentSignedBlobIds,
      clientSubmissionId: options.clientSubmissionId,
      pageContext: options.pageContext,
      questionAnswer: options.questionAnswer,
    })),
    signal: options.signal,
  }, options.responseTimeoutMs)
}

export async function createSessionOverHttp({
  workerId,
  content,
  agentId,
  source,
  fdeProjectId,
  businessProcessId,
  workflowType,
  attachments = [],
  options,
}: {
  workerId: string
  // Opcional: o kickoff da edição de Processos é gerado e enviado (oculto)
  // pelo servidor a partir de businessProcessId, sem mensagem do cliente.
  content?: string
  agentId?: string | null
  source?: string
  fdeProjectId?: string
  businessProcessId?: string
  workflowType?: string
  attachments?: File[]
  options?: SendOptions
}): Promise<AgentChatSessionDetail> {
  const sendOptions = options ?? {}
  throwIfAborted(sendOptions.signal)
  const attachmentSignedBlobIds = sendOptions.attachmentSignedBlobIds ?? await uploadAttachments(attachments, workerId)
  sendOptions.attachmentSignedBlobIds = attachmentSignedBlobIds
  throwIfAborted(sendOptions.signal)
  sendOptions.onUploadsComplete?.()
  throwIfAborted(sendOptions.signal)

  return sessionFromSubmissionRequest(chatPath('/agent_chat_sessions'), {
    method: 'POST',
    credentials: 'same-origin',
    headers: requestHeaders(),
    body: JSON.stringify(sessionPayload({
      workerId,
      content,
      agentId,
      source,
      fdeProjectId,
      businessProcessId,
      workflowType,
      attachmentSignedBlobIds,
      clientSubmissionId: sendOptions.clientSubmissionId,
      pageContext: sendOptions.pageContext,
    })),
    signal: sendOptions.signal,
  }, sendOptions.responseTimeoutMs)
}

function throwIfAborted(signal?: AbortSignal) {
  signal?.throwIfAborted()
}

function requestHeaders(): Record<string, string> {
  return {
    Accept: 'application/json',
    'X-CSRF-Token': csrfToken(),
    'Content-Type': 'application/json',
  }
}

async function fetchWithCsrfRetry(input: RequestInfo | URL, init: RequestInit): Promise<Response> {
  const response = await fetch(input, init)
  const refreshedToken = response.status === 422 ? response.headers.get('X-CSRF-Token') : null
  if (!refreshedToken) return response

  const meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
  if (meta) meta.content = refreshedToken

  return fetch(input, {
    ...init,
    headers: {
      ...headersWithoutCsrf(init.headers),
      'X-CSRF-Token': refreshedToken,
    },
  })
}

async function sessionFromSubmissionRequest(
  input: RequestInfo | URL,
  init: RequestInit,
  responseTimeoutMs?: number,
): Promise<AgentChatSessionDetail> {
  if (!responseTimeoutMs) return sessionFromResponse(await fetchWithCsrfRetry(input, init))

  const controller = new AbortController()
  let timedOut = false
  const abortFromParent = () => controller.abort(init.signal?.reason)
  if (init.signal?.aborted) abortFromParent()
  else init.signal?.addEventListener('abort', abortFromParent, { once: true })

  const timeoutId = window.setTimeout(() => {
    timedOut = true
    controller.abort()
  }, responseTimeoutMs)

  try {
    const response = await fetchWithCsrfRetry(input, { ...init, signal: controller.signal })
    return await sessionFromResponse(response)
  } catch (error) {
    if (timedOut && !init.signal?.aborted) {
      throw new TypeError('A resposta do chat demorou demais.')
    }
    throw error
  } finally {
    window.clearTimeout(timeoutId)
    init.signal?.removeEventListener('abort', abortFromParent)
  }
}

function headersWithoutCsrf(headers: HeadersInit | undefined): Record<string, string> {
  if (!headers) return {}

  const result: Record<string, string> = {}
  new Headers(headers).forEach((value, key) => {
    if (key.toLowerCase() !== 'x-csrf-token') result[key] = value
  })
  return result
}

async function uploadAttachments(
  attachments: File[],
  digitalWorkerId?: string | null,
): Promise<string[]> {
  if (attachments.length === 0) return []

  return Promise.all(attachments.map((file) => uploadAttachment(file, digitalWorkerId)))
}

function uploadAttachment(file: File, digitalWorkerId?: string | null): Promise<string> {
  const base = chatPath('/agent_chat_direct_uploads')
  const url = digitalWorkerId
    ? `${base}?digital_worker_id=${encodeURIComponent(digitalWorkerId)}`
    : base

  return new Promise((resolve, reject) => {
    const upload = new DirectUpload(file, url, undefined, {
      Accept: 'application/json',
    })

    upload.create((error: Error | string | null, blob?: DirectUploadBlob) => {
      if (error) {
        reject(new Error(typeof error === 'string' ? error : error.message))
        return
      }

      if (!blob?.signed_id) {
        reject(new Error('Não foi possível preparar o upload do anexo.'))
        return
      }

      resolve(blob.signed_id)
    })
  })
}

function messagePayload({ content, attachmentSignedBlobIds, clientSubmissionId, pageContext, questionAnswer }: {
  content: string
  attachmentSignedBlobIds: string[]
  clientSubmissionId?: string
  pageContext?: Record<string, unknown>
  questionAnswer?: UserQuestionAnswer
}) {
  return {
    content,
    client_submission_id: clientSubmissionId,
    attachment_signed_blob_ids: attachmentSignedBlobIds,
    page_context: pageContext,
    question_answer: questionAnswer,
  }
}

function sessionPayload({
  workerId,
  content,
  agentId,
  source,
  fdeProjectId,
  businessProcessId,
  workflowType,
  attachmentSignedBlobIds,
  clientSubmissionId,
  pageContext,
}: {
  workerId: string
  content?: string
  agentId?: string | null
  source?: string
  fdeProjectId?: string
  businessProcessId?: string
  workflowType?: string
  attachmentSignedBlobIds: string[]
  clientSubmissionId?: string
  pageContext?: Record<string, unknown>
}) {
  return {
    digital_worker_id: workerId,
    agent_id: agentId,
    initial_message: content,
    fde_project_id: fdeProjectId,
    business_process_id: businessProcessId,
    workflow_type: workflowType,
    source,
    client_submission_id: clientSubmissionId,
    attachment_signed_blob_ids: attachmentSignedBlobIds,
    page_context: pageContext,
  }
}

/** A reference is scoped to a persisted assistant message, never a free run lookup. */
export async function loadWorkflowReferenceOverHttp(
  sessionId: string,
  messageId: string,
  signal?: AbortSignal,
): Promise<AgentChatWorkflowReference> {
  const response = await fetch(`/dashboard/agent_chat_sessions/${encodeURIComponent(sessionId)}/workflow_reference?message_id=${encodeURIComponent(messageId)}`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    signal,
  })
  const payload = await response.json().catch(() => ({})) as {
    reference?: AgentChatWorkflowReference
    error?: string
  }
  if (!response.ok || !payload.reference) {
    throw new Error(typeof payload.error === 'string' ? payload.error : '')
  }
  return payload.reference
}
